<script lang="ts">
  // Big call to action for replay datasets: play the night from 7 PM, resume, or replay again.
  import { app } from '../../lib/app.svelte';
  import { fmtTime } from '../../lib/format';
  import { playDuration, SPEEDS, type Speed } from '../../lib/timeline';
  const ts = $derived(app.times);
  const atStart = $derived(app.viewIndex <= 0 && (app.clock === null || app.clock <= (ts[0] ?? 0)));
  const year = $derived(app.meta?.dataset?.year ?? app.replay ?? app.meta?.election.date.slice(0, 4));
  const label = $derived(app.meta?.dataset?.label ?? (app.final ? `${year} election night` : `${app.replay} replay`));
  const dur = (s: number) => { const ms = playDuration(ts, s); return ms >= 90_000 ? `${Math.round(ms / 60_000)} min` : `${Math.round(ms / 1000)} s`; };
  const hm = (t: number | null) => fmtTime(t).replace(' ET', '');
</script>

{#if (app.replay || app.final) && ts.length > 1}
  <section class="cta" aria-label="Replay controls">
    <div class="txt">
      <div class="kick">{label}</div>
      {#if app.playing}
        <h2>Playing election night · {hm(app.displayTime)}</h2>
        <p>Watch the counts, the needle and the maps update as the night unfolds. Pause any time, or change the speed:</p>
      {:else if app.atEnd}
        <h2>End of election night · {hm(ts.at(-1) ?? null)}</h2>
        <p>That’s how the {year} night unfolded. Watch it again, or scrub the timeline below to any moment.</p>
      {:else if atStart}
        {#if app.final}
          <h2>Replay election night</h2>
          <p>Every update we published, in the order it came in, from {hm(ts[0] ?? null)} to {hm(ts.at(-1) ?? null)}. Every number, needle and map changes as the night goes on. Pick a speed:</p>
        {:else}
          <h2>See what election night will look like</h2>
          <p>Real {year} results, revealed in a simulated reporting order from the moment polls closed at {hm(ts[0] ?? null)}. Every number, needle and map updates as the night goes on. Pick a speed:</p>
        {/if}
      {:else}
        <h2>Paused at {hm(app.displayTime)}</h2>
        <p>Resume the night, or drag the timeline below to jump around.</p>
      {/if}
      {#if !app.atEnd}
        <div class="speeds" role="group" aria-label="Replay speed">
          {#each SPEEDS as s}<button type="button" aria-pressed={app.speed === s} onclick={() => (app.speed = s as Speed)}><b>{s}×</b><span>{dur(s)}</span></button>{/each}
        </div>
      {/if}
    </div>
    <div class="acts">
      {#if app.playing}
        <button class="go" type="button" onclick={() => app.pause()}><svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1.5" fill="currentColor" /><rect x="14" y="5" width="4" height="14" rx="1.5" fill="currentColor" /></svg>Pause</button>
      {:else if app.atEnd}
        <button class="go" type="button" onclick={() => app.play()}><svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12a8 8 0 1 0 2.6-5.9M4 4v5h5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" /></svg>Replay again</button>
      {:else}
        <button class="go" type="button" onclick={() => app.play()}><svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.4-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" fill="currentColor" /></svg>{atStart ? 'Play election night' : 'Resume'}</button>
        {#if !atStart}<button class="ghost" type="button" onclick={() => { app.seek(ts[0]!); app.play(); }}>Start over</button>{/if}
      {/if}
    </div>
  </section>
{/if}

<style>
  .cta { margin-top: 20px; display: flex; gap: 20px 32px; align-items: center; justify-content: space-between; flex-wrap: wrap; background: var(--rp-bg); border: 2px solid var(--rp); border-radius: var(--r-card); padding: 22px 24px; }
  .txt { flex: 1 1 420px; min-width: 0; }
  .kick { font: 800 0.72rem/1 var(--font); letter-spacing: 0.12em; text-transform: uppercase; color: var(--rp-ink); }
  h2 { font-size: clamp(1.4rem, 1.1rem + 1.2vw, 1.9rem); margin-top: 8px; }
  p { margin: 6px 0 0; color: var(--ink-2); max-width: 640px; }
  .speeds { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
  .speeds button { display: inline-flex; flex-direction: column; align-items: flex-start; gap: 2px; border: 1.5px solid var(--rp-line); background: var(--card); border-radius: 12px; padding: 7px 12px; cursor: pointer; color: var(--ink); }
  .speeds button b { font: 700 0.95rem/1 var(--display); }
  .speeds button span { font-size: 0.72rem; color: var(--ink-3); }
  .speeds button[aria-pressed='true'] { border-color: var(--rp); background: var(--rp); color: #1a1205; }
  .speeds button[aria-pressed='true'] span { color: #1a1205; }
  .acts { display: flex; gap: 10px; flex-wrap: wrap; }
  .go { display: inline-flex; align-items: center; gap: 10px; border: 0; background: #1a1205; color: var(--rp); font: 700 1.1rem/1 var(--display); padding: 16px 24px; border-radius: 999px; cursor: pointer; box-shadow: 0 6px 18px color-mix(in oklab, var(--rp) 35%, transparent); }
  :global(:root[data-theme='dark']) .go { background: var(--rp); color: #1a1205; }
  @media (prefers-color-scheme: dark) { :global(:root:not([data-theme='light'])) .go { background: var(--rp); color: #1a1205; } }
  .go:hover { filter: brightness(1.1); }
  .ghost { border: 1.5px solid var(--rp); background: transparent; color: var(--rp-ink); font-weight: 700; padding: 14px 18px; border-radius: 999px; cursor: pointer; }
  @media (max-width: 640px) { .cta { padding: 18px; } .acts, .go { width: 100%; } .go { justify-content: center; } }
</style>
